import React from "react";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global error boundary â€” catches any unhandled render error in the React
 * tree and shows a recovery screen instead of a blank page. Wraps the
 * entire app in index.tsx.
 */
class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // If Sentry/Bugsnag is ever added, report here.
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "var(--app-vh)",
            padding: 32,
            fontFamily: "Poppins, sans-serif",
            textAlign: "center",
            gap: 16,
          }}
        >
          <div style={{ fontSize: 48 }}>âš ï¸</div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
            Something went wrong
          </h2>
          <p style={{ margin: 0, fontSize: 14, color: "#64748B", maxWidth: 440 }}>
            An unexpected error occurred. You can try reloading the page.
            If the problem persists, contact support.
          </p>
          {this.state.error && (
            <pre
              style={{
                fontSize: 12,
                color: "#94A3B8",
                background: "#F8FAFC",
                borderRadius: 8,
                padding: "10px 16px",
                maxWidth: 520,
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {this.state.error.message}
            </pre>
          )}
          <button
            onClick={this.handleReload}
            style={{
              marginTop: 8,
              padding: "10px 24px",
              fontSize: 14,
              fontWeight: 500,
              borderRadius: 10,
              border: "none",
              background: "#2563EB",
              color: "#FFF",
              cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
